/**
 * Composite Analyzer: ATM Parameter Direction (Phase 17)
 *
 * Cross-ATM pattern detector. Looks across ALL of the trader's ATM templates
 * and asks: "is there a parameter the trader keeps adjusting in the same
 * direction, and is the adjustment consistently associated with WR
 * movement in that direction?"
 *
 * Example output:
 *
 *   "Across 4 templates, widening Trail SL has lifted WR every time
 *    (12→16, 20→24, 18→22, 14→18 — avg +18pp). If you're tuning a fifth
 *    template and Trail SL is on the table, the pattern would default to
 *    wider unless you have a specific read against it."
 *
 * Without this analyzer, the version-change knowledge is per-template and
 * the trader can't see that they have a consistent edge-direction across
 * their playbook. This is a "meta-edge" — knowing HOW to iterate, not
 * just WHAT to iterate on a single template.
 *
 * Methodology:
 *   1. For each ATM template with ≥2 versions, compute every consecutive
 *      pairwise change. For each NUMERIC change (stopLossTicks, targetTicks,
 *      trail_frequency, etc.), record { field, direction, wrDelta }.
 *   2. Aggregate by (field, direction) — e.g., ("trail_stopLoss", "wider").
 *   3. Surface buckets where:
 *      - ≥3 templates show the same field+direction change, AND
 *      - ≥75% of those changes improved WR by ≥MIN_WR_DELTA
 *      → strong directional signal worth highlighting (positive observation)
 *      OR
 *      - ≥75% REGRESSED → cross-template pattern worth pausing on before
 *        the next adjustment (NOT a "stop doing this" verdict — see Voice
 *        rebalance note below for why)
 *
 * Reads:
 *   ctx.allTrades (with netPnl enriched per the day-scope pattern)
 *   ctx.ntTemplateVersions — keyed by atmName
 *
 * ─── Voice rebalance (2026-05-27) ───────────────────────────────────────
 *
 * The "regressed" path in this analyzer used to read like the harshest
 * thing the Coach said to the trader. The pre-rebalance text was:
 *
 *   title:  "Cross-template leak — tightening Trail SL has hurt 4/5 times"
 *   action: "Stop tightening Trail SL. The data is unanimous — whatever
 *            instinct keeps pulling you that direction is wrong for your
 *            style."
 *
 * Three things wrong with that framing:
 *   1. "Leak" frames iteration as failure. A repeated direction with WR
 *      drops is a pattern worth examining, not a defect.
 *   2. "Stop" is an order. The trader's the one doing the work; the coach
 *      surfaces the pattern and the question, then hands the next move
 *      back.
 *   3. "Whatever instinct ... is wrong for your style" is a judgment about
 *      the trader's character, not about the data. The instinct exists for
 *      a reason — the data can describe the result but can't tell us
 *      whether the instinct is right or wrong in general.
 *
 * The post-rebalance text below frames the same statistical fact as a
 * cross-template signal worth investigating before the next adjustment,
 * and asks the trader to articulate WHAT they're optimizing for when they
 * pull that direction.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types.js';
import { findVersionForTrade, diffAtmVersions, diffFieldLabel } from '../../../ntTemplateParser.js';

export const key = 'atmParamDirection';
export const tier = 'free';

const MIN_TEMPLATES_FOR_SIGNAL = 3;     // need ≥3 templates showing the same direction
const MIN_TRADES_PER_VERSION = 3;        // per-version sample-size floor (matches atmVersionTrend)
const MIN_WR_DELTA = 0.10;               // change must move WR ≥10pp to "count"
const MIN_AGREEMENT_PCT = 0.75;          // ≥75% of templates agree on direction's effect

const fmtPct = (n) => `${Math.round(n * 100)}%`;

const computeWR = (trades) => {
  if (!Array.isArray(trades) || trades.length === 0) return 0;
  const wins = trades.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
  return wins / trades.length;
};

/**
 * For a single ATM template, walk its versions chronologically and emit
 * per-field-change records: { field, bracket, step, oldVal, newVal,
 * direction, wrDelta }. Only includes pairs where both sides have ≥
 * MIN_TRADES_PER_VERSION trades.
 */
const extractChangesForTemplate = (atmName, versions, tradesForThisAtm) => {
  if (!Array.isArray(versions) || versions.length < 2) return [];

  // Attribute trades to versions
  const byVersion = new Map();
  for (const t of tradesForThisAtm) {
    const v = findVersionForTrade(t, versions);
    if (!v?.versionId) continue;
    if (!byVersion.has(v.versionId)) byVersion.set(v.versionId, []);
    byVersion.get(v.versionId).push(t);
  }

  // Walk in chronological order (versions are newest-first → reverse)
  const chrono = [...versions].reverse();
  const records = [];

  for (let i = 1; i < chrono.length; i++) {
    const prior = chrono[i - 1];
    const current = chrono[i];
    const priorTrades = byVersion.get(prior.versionId) || [];
    const currentTrades = byVersion.get(current.versionId) || [];
    if (priorTrades.length < MIN_TRADES_PER_VERSION) continue;
    if (currentTrades.length < MIN_TRADES_PER_VERSION) continue;

    const wrDelta = computeWR(currentTrades) - computeWR(priorTrades);
    const changes = diffAtmVersions(prior, current);

    // Task A.2 — capture the templateType of the version where the change
    // landed (the "current" side of the prior→current pair). Stamped on each
    // record so aggregateByFieldDirection can surface the type breakdown in
    // evidence — e.g., a "widening Trail SL" cross-template signal that's
    // driven entirely by AT-template iterations is a different read than one
    // mixing AT and TP. Today's plumbing exposes the breakdown without
    // changing bucket aggregation; A.3 may split buckets by type if signals
    // cross-contaminate in practice.
    const recordTemplateType = current?.snapshot?.templateType || null;
    for (const c of changes) {
      // Numeric changes only — direction means nothing for "bracketAdded"
      // or exitType swaps. Those are covered by atmVersionComparison.
      if (typeof c.old !== 'number' || typeof c.new !== 'number') continue;
      const direction = c.new > c.old ? 'increased' : (c.new < c.old ? 'decreased' : null);
      if (!direction) continue;
      records.push({
        atmName,
        field: c.field,
        bracket: c.bracket ?? null,
        step: c.step ?? null,
        oldVal: c.old,
        newVal: c.new,
        direction,
        wrDelta,
        templateType: recordTemplateType,
      });
    }
  }

  return records;
};

/**
 * Aggregate per-field+direction records across all templates.
 * Returns a map: `${field}|${direction}` → { records[], improvedCount,
 * regressedCount, neutralCount, avgWrDelta, templatesCount }.
 */
const aggregateByFieldDirection = (allRecords) => {
  const buckets = new Map();
  for (const r of allRecords) {
    const key = `${r.field}|${r.direction}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        field: r.field,
        direction: r.direction,
        records: [],
        templates: new Set(),
      });
    }
    const b = buckets.get(key);
    b.records.push(r);
    b.templates.add(r.atmName);
  }

  // Compute summary stats per bucket
  const out = [];
  for (const b of buckets.values()) {
    let improved = 0, regressed = 0, neutral = 0;
    let wrSum = 0;
    // Task A.2 — templateType breakdown surfaced in evidence. Doesn't affect
    // the signal threshold today; it lets downstream consumers see whether
    // the pattern is concentrated in one template style or spread across.
    const typeBreakdown = { AT: 0, TP: 0, Hybrid: 0, Unknown: 0 };
    for (const r of b.records) {
      if (r.wrDelta >= MIN_WR_DELTA) improved++;
      else if (r.wrDelta <= -MIN_WR_DELTA) regressed++;
      else neutral++;
      wrSum += r.wrDelta;
      const tt = r.templateType || 'Unknown';
      typeBreakdown[tt] = (typeBreakdown[tt] ?? 0) + 1;
    }
    out.push({
      field: b.field,
      direction: b.direction,
      records: b.records,
      templatesCount: b.templates.size,
      improvedCount: improved,
      regressedCount: regressed,
      neutralCount: neutral,
      avgWrDelta: wrSum / b.records.length,
      typeBreakdown,
    });
  }
  return out;
};

export const analyze = (ctx) => {
  const { allTrades = [], ntTemplateVersions = {} } = ctx || {};
  if (!Array.isArray(allTrades) || allTrades.length === 0) return null;
  if (!ntTemplateVersions || typeof ntTemplateVersions !== 'object') return null;
  const atmNames = Object.keys(ntTemplateVersions);
  if (atmNames.length === 0) return null;

  // Live-trade gate: cross-template direction detection compares per-version
  // WR (computeWR) before vs after a parameter change. Open trades have
  // unrealized P&L which isn't a realized outcome — including them in the
  // post-change cohort would fabricate a phantom "this direction works"
  // signal while the user is still in the trade. Matches summarizePnl
  // semantics. Filter once so all per-template subsets inherit the gate.
  const closedAllTrades = allTrades.filter(t => t?.isOpen !== true);
  if (closedAllTrades.length === 0) return null;

  // Gather per-template change records
  const allRecords = [];
  for (const atmName of atmNames) {
    const versions = ntTemplateVersions[atmName];
    if (!Array.isArray(versions) || versions.length < 2) continue;
    const tradesForThisAtm = closedAllTrades.filter(t => t.atmStrategy === atmName);
    if (tradesForThisAtm.length === 0) continue;
    allRecords.push(...extractChangesForTemplate(atmName, versions, tradesForThisAtm));
  }
  if (allRecords.length === 0) return null;

  const buckets = aggregateByFieldDirection(allRecords);

  // Find buckets that meet the signal criteria
  const observations = [];
  for (const b of buckets) {
    if (b.templatesCount < MIN_TEMPLATES_FOR_SIGNAL) continue;
    const totalDirectional = b.improvedCount + b.regressedCount;
    if (totalDirectional === 0) continue;

    const improvedShare = b.improvedCount / b.records.length;
    const regressedShare = b.regressedCount / b.records.length;

    const fieldLabel = diffFieldLabel(b.field);
    // ATM parameter direction → trader-natural verb. Frequency is the edge
    // case: HIGHER frequency value = stops adjust LESS often = LOOSER trail
    // (the trade gets more room). Lower frequency = more responsive trail =
    // tighter. This is INVERTED from Ticks / Trigger / stopLoss, where
    // bigger number = wider distance. Splitting frequency out so the
    // analyzer text matches the trader's mental model — otherwise the LLM
    // synthesis flattens "widening Trail Frequency" and "tightening
    // stopLossTicks" into one ambiguous "tightening trail parameters"
    // paragraph (the v7.6.0 bug Chris caught: 12→24 frequency change shown
    // as part of a "tightening trail parameters" pattern when it was the
    // opposite direction).
    const directionWord = (() => {
      if (b.field.includes('frequency')) {
        // Frequency: increased = looser, decreased = tighter
        return b.direction === 'increased' ? 'loosening' : 'tightening';
      }
      if (b.field.includes('Ticks') || b.field.includes('Trigger') || b.field.includes('stopLoss')) {
        // Ticks / Trigger / stopLoss: increased = wider distance, decreased = tighter
        return b.direction === 'increased' ? 'widening' : 'tightening';
      }
      // Generic numeric field — just say increasing / decreasing
      return b.direction === 'increased' ? 'increasing' : 'decreasing';
    })();

    if (improvedShare >= MIN_AGREEMENT_PCT) {
      // Cross-template winning-direction pattern.
      // The regex /widening|increased/i in atmParamDirection.test.mjs's
      // edge-case test probes this title; keep the directionWord present.
      const samples = b.records.slice(0, 4).map(r => `${r.atmName.slice(0, 24)} (${r.oldVal}→${r.newVal})`).join(', ');
      observations.push(makeObservation({
        id: `${key}:${b.field}-${b.direction}:edge`,
        analyzer: key,
        scope: 'global',
        scopeId: `param:${b.field}:${b.direction}`,
        severity: SEVERITY.SUCCESS,
        category: CATEGORY.SETUP,
        title: `Cross-template signal — ${directionWord} ${fieldLabel} has lifted WR on ${b.improvedCount}/${b.records.length} templates`,
        detail: `Across ${b.templatesCount} templates, ${directionWord} ${fieldLabel} has been associated with WR lift every time it cleared the threshold (avg +${fmtPct(Math.abs(b.avgWrDelta))} per change). Examples: ${samples}.`,
        action: `When you're tuning another template and ${fieldLabel} is on the table, the cross-template data so far points to ${directionWord} as the direction worth defaulting to — unless you have a specific read against it on that template. If one template breaks the pattern, that's a useful data point too; the meta-edge is knowing when to follow it and when to override.`,
        evidence: {
          field: b.field,
          direction: b.direction,
          improvedCount: b.improvedCount,
          regressedCount: b.regressedCount,
          neutralCount: b.neutralCount,
          templatesCount: b.templatesCount,
          avgWrDelta: b.avgWrDelta,
          samples: b.records.slice(0, 8),
          typeBreakdown: b.typeBreakdown,  // Task A.2 — AT/TP/Hybrid/Unknown counts
        },
        confidence: 0.75,
        priority: 82,
      }));
    } else if (regressedShare >= MIN_AGREEMENT_PCT) {
      // Cross-template direction-with-WR-drops pattern. Pre-rebalance this
      // was framed as a "leak" with a "Stop doing this" action — see the
      // Voice rebalance note in the file header for why that framing was
      // wrong. New framing names the same statistical signal as a pattern
      // worth pausing on, then hands the next decision back to the trader.
      const samples = b.records.slice(0, 4).map(r => `${r.atmName.slice(0, 24)} (${r.oldVal}→${r.newVal})`).join(', ');
      observations.push(makeObservation({
        id: `${key}:${b.field}-${b.direction}:leak`,
        analyzer: key,
        scope: 'global',
        scopeId: `param:${b.field}:${b.direction}`,
        severity: SEVERITY.WARNING,
        category: CATEGORY.SETUP,
        title: `Cross-template pattern — ${directionWord} ${fieldLabel} has been associated with WR drops on ${b.regressedCount}/${b.records.length} templates`,
        detail: `Across ${b.templatesCount} templates, ${directionWord} ${fieldLabel} has been associated with WR drops (avg ${fmtPct(b.avgWrDelta)} per change). Examples: ${samples}.`,
        action: `Worth pausing before the next ${directionWord} adjustment to that param — and asking what the read is that keeps pulling you that direction. If you can articulate it ("I keep tightening because I'm tired of giving back winners"), you'll know whether to override the cross-template pattern or follow it. A small-account test of the opposite direction on one template would disambiguate the question in real trades.`,
        evidence: {
          field: b.field,
          direction: b.direction,
          improvedCount: b.improvedCount,
          regressedCount: b.regressedCount,
          neutralCount: b.neutralCount,
          templatesCount: b.templatesCount,
          avgWrDelta: b.avgWrDelta,
          samples: b.records.slice(0, 8),
          typeBreakdown: b.typeBreakdown,  // Task A.2 — AT/TP/Hybrid/Unknown counts
        },
        confidence: 0.78,
        priority: 88,
      }));
    }
  }

  return observations.length > 0 ? observations : null;
};

// Test-friendly exports
export {
  computeWR,
  extractChangesForTemplate,
  aggregateByFieldDirection,
  MIN_TEMPLATES_FOR_SIGNAL,
  MIN_TRADES_PER_VERSION,
  MIN_WR_DELTA,
  MIN_AGREEMENT_PCT,
};
